import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "../../../App.css";
import pogImage from "../../../assets/casino/pog.png";
import pogfortsBackground from "../../../assets/PogfortsHome.png";
import reel01 from "../../../assets/batrunner/reels/reel01.png";
import reel02 from "../../../assets/batrunner/reels/reel02.png";
import reel03 from "../../../assets/batrunner/reels/reel03.png";
import reel04 from "../../../assets/batrunner/reels/reel04.png";
import reel05 from "../../../assets/batrunner/reels/reel05.png";
import reel06 from "../../../assets/batrunner/reels/reel06.png";
import reel07 from "../../../assets/batrunner/reels/reel07.png";
import reel08 from "../../../assets/batrunner/reels/reel08.png";
import reel09 from "../../../assets/batrunner/reels/reel09.png";
import reel10 from "../../../assets/batrunner/reels/reel10.png";
import reel11 from "../../../assets/batrunner/reels/reel11.png";
import batrunnerBgm from "../../../assets/batrunner/sounds/BatrunnerBGM.mp3";
import collectSfx from "../../../assets/batrunner/sounds/CollectSFX.mp3";
import collisionSfx from "../../../assets/batrunner/sounds/CollisionSFX.mp3";

const LOCAL_PREVIEW_HOSTS = new Set(["localhost", "127.0.0.1"]);
const DEV_PREVIEW_BALANCE = 50000;

function useArcadeWalletAutoScale(displayValue) {
  const frameRef = useRef(null);
  const valueRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const frame = frameRef.current;
    const value = valueRef.current;
    if (!frame || !value) return undefined;

    const measure = () => {
      const availableWidth = Math.max(1, frame.clientWidth);
      const naturalWidth = Math.max(1, value.scrollWidth);
      const nextScale = Math.min(1, availableWidth / naturalWidth);
      setScale((current) =>
        Math.abs(current - nextScale) < 0.002 ? current : nextScale
      );
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(frame);
      observer.observe(value);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure, { passive: true });
    return () => window.removeEventListener("resize", measure);
  }, [displayValue]);

  return { frameRef, valueRef, scale };
}
const SCORE_PER_SECOND = 125;
const POG_SPAWN_MIN_MS = 5000;
const POG_SPAWN_MAX_MS = 9500;
const POG_TIME_SEED_XOR = 0xa53a9e1d;
const POG_X_SEED_XOR = 0x1f123bb5;
const ROCK_SEED_XOR = 0x9e3779b9;
const HUD_REFRESH_MS = 70;
const TAP_IMPULSE_MS = 170;
const MAX_DT_SECONDS = 0.034;
const REEL_SCROLL_FACTOR = 0.155;
const REEL_SOURCE_WIDTH = 1536;
const REEL_SOURCE_HEIGHT = 1059;
const BATRUNNER_REEL_SOURCES = [
  reel01,
  reel02,
  reel03,
  reel04,
  reel05,
  reel06,
  reel07,
  reel08,
  reel09,
  reel10,
  reel11,
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function formatPogs(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? Math.max(0, Math.floor(number)).toLocaleString("en-GB") : "0";
}

function createRng(seed) {
  let state = Number(seed) >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randomBetween(rng, min, max) {
  return min + (max - min) * rng();
}

function randomIntInclusive(rng, min, max) {
  return Math.floor(rng() * (max - min + 1)) + min;
}

function isLocalPreviewHost() {
  return LOCAL_PREVIEW_HOSTS.has(window.location.hostname);
}

function isMobileLikeDevice() {
  return (
    window.matchMedia?.("(pointer: coarse)")?.matches ||
    window.matchMedia?.("(max-width: 1024px)")?.matches ||
    window.innerWidth <= 1024
  );
}

async function enterMobileFullscreen() {
  if (!isMobileLikeDevice()) return;

  const target = document.documentElement;
  const requestFullscreen =
    target.requestFullscreen ||
    target.webkitRequestFullscreen ||
    target.msRequestFullscreen;

  if (typeof requestFullscreen !== "function") return;

  try {
    await requestFullscreen.call(target);
  } catch (error) {
    // CSS fixed-viewport game mode remains the guaranteed fallback.
  }
}

async function exitBrowserFullscreen() {
  const exitFullscreen =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.msExitFullscreen;

  const isFullscreen =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement;

  if (!isFullscreen || typeof exitFullscreen !== "function") return;

  try {
    await exitFullscreen.call(document);
  } catch (error) {
    // The site shell can still be restored even if the browser rejects exitFullscreen.
  }
}

async function readJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(payload?.error || "Batrunner request failed");
    error.code = payload?.code || "BATRUNNER_REQUEST_FAILED";
    error.status = response.status;
    throw error;
  }

  return payload;
}

async function getBatrunnerProfile() {
  const response = await fetch("/api/user/profile", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  return readJsonResponse(response);
}

async function startBatrunnerRun() {
  const response = await fetch("/api/batrunner/start", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });

  return readJsonResponse(response);
}

async function settleBatrunnerRun(payload) {
  const response = await fetch("/api/batrunner/settle", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  return readJsonResponse(response);
}

function getReelTrackState(game) {
  const reelWidth = Math.max(1, game.width);
  const reelHeight = Math.max(
    1,
    reelWidth * (REEL_SOURCE_HEIGHT / REEL_SOURCE_WIDTH)
  );
  const cycleHeight = reelHeight * BATRUNNER_REEL_SOURCES.length;
  const worldSpeed = getWorldSpeed(game);
  const travel = ((game.elapsedMs / 1000) * worldSpeed * REEL_SCROLL_FACTOR) % cycleHeight;

  return {
    reelWidth,
    reelHeight,
    cycleHeight,
    travel,
  };
}

function isAsteroidReelPhase(game) {
  const { reelHeight, travel } = getReelTrackState(game);

  // With the downward-scrolling authored stack, Reel07's bottom edge reaches
  // the top of the viewport at 5 reel-heights of travel. The next cycle's
  // Reel01 bottom edge reaches the top at 10 reel-heights. That exact interval
  // is the space/asteroid visual phase.
  return travel >= reelHeight * 5 && travel < reelHeight * 10;
}

function drawBackground(ctx, game) {
  const { width, height, elapsedMs } = game;
  const reels = Array.isArray(game.reelImages) ? game.reelImages : [];

  ctx.save();
  ctx.fillStyle = "#080004";
  ctx.fillRect(0, 0, width, height);

  // Every source reel is the same 1536×1059 canvas and has already been
  // authored to join perfectly to the next reel. We therefore scale every
  // reel by the exact same factor and stack them without cropping, mirroring,
  // randomisation or cross-fading. This preserves the artist-made seams.
  const { reelWidth, reelHeight, cycleHeight, travel } = getReelTrackState(game);

  let imageDrawn = false;

  // The world scrolls DOWNWARD. To make the visible sequence advance in the
  // authored order (reel01 → reel02 → ... → reel11 → reel01), the *next* reel
  // must physically sit ABOVE the current one so that it enters from the top as
  // travel increases. Therefore the deterministic stack uses -index rather than
  // +index. There is no shuffling, random reel selection or mirroring here.
  //
  // At travel = 0:      reel01 is at y = 0 and reel02 is directly above it.
  // At travel = 1 reel: reel02 is at y = 0.
  // ...
  // At travel = 10:     reel11 is at y = 0.
  // The modulo reset then returns cleanly to reel01.
  for (let cycle = -1; cycle <= 1; cycle += 1) {
    const cycleY = travel + cycle * cycleHeight;

    for (let index = 0; index < BATRUNNER_REEL_SOURCES.length; index += 1) {
      const image = reels[index];
      if (!image || !image.complete || !image.naturalWidth || !image.naturalHeight) {
        continue;
      }

      const y = cycleY - index * reelHeight;
      if (y >= height || y + reelHeight <= 0) continue;

      // Fractional canvas transforms can occasionally expose a sub-pixel hairline
      // between perfectly matching images. A tiny destination overlap removes that
      // rasterisation artefact without changing the visible composition.
      ctx.drawImage(image, 0, y, reelWidth, reelHeight + 0.75);
      imageDrawn = true;
    }
  }

  if (!imageDrawn) {
    const fallback = ctx.createLinearGradient(0, 0, 0, height);
    fallback.addColorStop(0, "#240006");
    fallback.addColorStop(0.45, "#7b090e");
    fallback.addColorStop(1, "#090003");
    ctx.fillStyle = fallback;
    ctx.fillRect(0, 0, width, height);
  }

  // Keep the existing Batrunner atmospheric depth treatment above the authored
  // reel artwork. It moves independently of the images and does not alter the
  // reel ordering or seam geometry.
  const depthVeil = ctx.createLinearGradient(0, 0, 0, height);
  depthVeil.addColorStop(0, "rgba(0, 0, 0, 0.38)");
  depthVeil.addColorStop(0.42, "rgba(0, 0, 0, 0.25)");
  depthVeil.addColorStop(1, "rgba(0, 0, 0, 0.20)");
  ctx.fillStyle = depthVeil;
  ctx.fillRect(0, 0, width, height);

  const heat = ctx.createLinearGradient(0, 0, 0, height);
  heat.addColorStop(0, "rgba(35, 0, 4, 0.02)");
  heat.addColorStop(0.58, "rgba(160, 5, 12, 0.05)");
  heat.addColorStop(1, "rgba(255, 35, 14, 0.12)");
  ctx.fillStyle = heat;
  ctx.fillRect(0, 0, width, height);

  const smokeTime = elapsedMs * 0.00018;
  for (let i = 0; i < 9; i += 1) {
    const px = ((i * 173 + Math.sin(smokeTime * (1 + i * 0.05)) * 140) % (width + 280)) - 140;
    const py = ((elapsedMs * (0.018 + i * 0.0018) + i * 149) % (height + 280)) - 140;
    const radius = 100 + (i % 4) * 38;
    const smoke = ctx.createRadialGradient(px, py, 0, px, py, radius);
    smoke.addColorStop(0, "rgba(16, 4, 7, 0.24)");
    smoke.addColorStop(0.55, "rgba(8, 0, 2, 0.12)");
    smoke.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = smoke;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function getWorldSpeed(game) {
  const seconds = game.elapsedMs / 1000;
  const base = game.height * 0.60;
  const ramp = game.height * Math.min(0.34, seconds * 0.0027);
  return base + ramp;
}

function updateEmbers(game, dt) {
  const worldSpeed = getWorldSpeed(game);

  game.embers.forEach((ember) => {
    ember.y += (worldSpeed * ember.speedFactor + 80) * dt;
    ember.x += Math.sin((game.elapsedMs * 0.002 + ember.phase)) * ember.drift * dt;

    if (ember.y > game.height + 20) {
      ember.y = -20 - ember.size;
      ember.x = randomBetween(game.visualRng, 0, game.width);
      // Existing particles keep their original appearance. Only particles that
      // re-enter after a reel transition adopt the current environment style.
      ember.isStar = Boolean(game.asteroidMode);
    }
  });
}

function drawEmbers(ctx, game) {
  ctx.save();
  ctx.globalCompositeOperation = "lighter";

  game.embers.forEach((ember) => {
    const glow = ctx.createRadialGradient(
      ember.x,
      ember.y,
      0,
      ember.x,
      ember.y,
      ember.size * 4
    );
    if (ember.isStar) {
      glow.addColorStop(0, "rgba(255, 255, 255, 1)");
      glow.addColorStop(0.22, "rgba(245, 248, 255, 0.94)");
      glow.addColorStop(1, "rgba(220, 230, 255, 0)");
    } else {
      glow.addColorStop(0, "rgba(255, 230, 120, 0.95)");
      glow.addColorStop(0.2, "rgba(255, 80, 28, 0.90)");
      glow.addColorStop(1, "rgba(219, 51, 51, 0)");
    }
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(ember.x, ember.y, ember.size * 4, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.restore();
}

function drawSpeedStreaks(ctx, game) {
  const intensity = clamp(game.elapsedMs / 55000, 0, 1);
  if (intensity <= 0.02) return;

  ctx.save();
  ctx.globalAlpha = 0.08 + intensity * 0.14;
  ctx.lineWidth = 1;

  for (let i = 0; i < 18; i += 1) {
    const x = ((i * 97 + 41) % Math.max(1, game.width));
    const offset = (game.elapsedMs * (0.5 + i * 0.013) + i * 83) % (game.height + 180);
    const y = offset - 90;
    const length = 32 + (i % 5) * 16 + intensity * 45;
    const gradient = ctx.createLinearGradient(x, y, x, y + length);
    gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
    gradient.addColorStop(1, "rgba(255, 95, 55, 0.9)");
    ctx.strokeStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + length);
    ctx.stroke();
  }

  ctx.restore();
}

function drawCorridor(ctx, game) {
  const left = game.corridorLeft;
  const right = game.corridorRight;

  ctx.save();

  const leftShade = ctx.createLinearGradient(0, 0, left + 90, 0);
  leftShade.addColorStop(0, "rgba(0, 0, 0, 0.88)");
  leftShade.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = leftShade;
  ctx.fillRect(0, 0, Math.max(0, left + 90), game.height);

  const rightShade = ctx.createLinearGradient(right - 90, 0, game.width, 0);
  rightShade.addColorStop(0, "rgba(0, 0, 0, 0)");
  rightShade.addColorStop(1, "rgba(0, 0, 0, 0.88)");
  ctx.fillStyle = rightShade;
  ctx.fillRect(Math.max(0, right - 90), 0, game.width - Math.max(0, right - 90), game.height);

  ctx.strokeStyle = "rgba(255, 67, 67, 0.24)";
  ctx.lineWidth = 2;
  ctx.shadowColor = "rgba(255, 30, 30, 0.65)";
  ctx.shadowBlur = 18;
  ctx.beginPath();
  ctx.moveTo(left, 0);
  ctx.lineTo(left, game.height);
  ctx.moveTo(right, 0);
  ctx.lineTo(right, game.height);
  ctx.stroke();

  ctx.restore();
}

function buildRockShape(rng) {
  const pointCount = randomIntInclusive(rng, 8, 12);
  return Array.from({ length: pointCount }, (_, index) => ({
    angle: (index / pointCount) * Math.PI * 2,
    scale: randomBetween(rng, 0.72, 1.14),
  }));
}

function drawRock(ctx, rock) {
  ctx.save();
  ctx.translate(rock.x, rock.y);
  ctx.rotate(rock.rotation);

  ctx.shadowColor = rock.isAsteroid
    ? "rgba(225, 232, 242, 0.26)"
    : "rgba(255, 31, 18, 0.30)";
  ctx.shadowBlur = 16;

  const rockGradient = ctx.createRadialGradient(
    -rock.radius * 0.28,
    -rock.radius * 0.34,
    rock.radius * 0.08,
    0,
    0,
    rock.radius * 1.15
  );
  if (rock.isAsteroid) {
    rockGradient.addColorStop(0, "#d8dce1");
    rockGradient.addColorStop(0.28, "#aaaeb4");
    rockGradient.addColorStop(0.70, "#686d74");
    rockGradient.addColorStop(1, "#33373d");
  } else {
    rockGradient.addColorStop(0, "#6d6060");
    rockGradient.addColorStop(0.28, "#32292a");
    rockGradient.addColorStop(0.70, "#171112");
    rockGradient.addColorStop(1, "#080606");
  }
  ctx.fillStyle = rockGradient;

  ctx.beginPath();
  rock.shape.forEach((point, index) => {
    const x = Math.cos(point.angle) * rock.radius * point.scale;
    const y = Math.sin(point.angle) * rock.radius * point.scale;
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = rock.isAsteroid
    ? "rgba(245, 248, 255, 0.28)"
    : "rgba(255, 81, 58, 0.18)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.globalAlpha = 0.58;
  ctx.fillStyle = rock.isAsteroid
    ? "rgba(255, 255, 255, 0.30)"
    : "rgba(255, 255, 255, 0.13)";
  ctx.beginPath();
  ctx.ellipse(
    -rock.radius * 0.24,
    -rock.radius * 0.24,
    rock.radius * 0.30,
    rock.radius * 0.13,
    -0.55,
    0,
    Math.PI * 2
  );
  ctx.fill();

  ctx.restore();
}

function drawPog(ctx, game, coin) {
  const radius = coin.radius;
  const spin = Math.cos(coin.rotation);
  const scaleX = Math.max(0.18, Math.abs(spin));

  ctx.save();
  ctx.translate(coin.x, coin.y);
  ctx.scale(scaleX, 1);

  ctx.shadowColor = "rgba(255, 205, 39, 0.95)";
  ctx.shadowBlur = 24;

  if (game.pogImage?.complete && game.pogImage?.naturalWidth) {
    ctx.drawImage(game.pogImage, -radius, -radius, radius * 2, radius * 2);
  } else {
    const fallback = ctx.createRadialGradient(-radius * 0.3, -radius * 0.3, 0, 0, 0, radius);
    fallback.addColorStop(0, "#fff2a8");
    fallback.addColorStop(0.35, "#ffcf31");
    fallback.addColorStop(1, "#9e5200");
    ctx.fillStyle = fallback;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.28;
  ctx.strokeStyle = "#ffd754";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(coin.x, coin.y, radius + 8 + Math.sin(coin.rotation * 2) * 3, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawBat(ctx, game) {
  const player = game.player;
  const size = player.size;
  const bank = clamp(player.vx / 620, -1, 1) * 0.20;
  const flap = Math.sin(game.elapsedMs * 0.015) * size * 0.055;

  ctx.save();
  ctx.translate(player.x, player.y);
  ctx.rotate(bank);

  ctx.shadowColor = "rgba(255, 31, 31, 0.58)";
  ctx.shadowBlur = 22;

  const wingGradient = ctx.createLinearGradient(0, -size * 0.26, 0, size * 0.26);
  wingGradient.addColorStop(0, "#571016");
  wingGradient.addColorStop(0.42, "#1a090c");
  wingGradient.addColorStop(1, "#050304");
  ctx.fillStyle = wingGradient;
  ctx.strokeStyle = "rgba(255, 71, 71, 0.38)";
  ctx.lineWidth = Math.max(1, size * 0.018);

  ctx.beginPath();
  ctx.moveTo(-size * 0.08, -size * 0.10);
  ctx.bezierCurveTo(-size * 0.30, -size * 0.34 - flap, -size * 0.50, -size * 0.20 - flap, -size * 0.58, -size * 0.02);
  ctx.lineTo(-size * 0.43, size * 0.02);
  ctx.lineTo(-size * 0.34, size * 0.18 + flap * 0.25);
  ctx.lineTo(-size * 0.22, size * 0.08);
  ctx.lineTo(-size * 0.10, size * 0.20);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(size * 0.08, -size * 0.10);
  ctx.bezierCurveTo(size * 0.30, -size * 0.34 - flap, size * 0.50, -size * 0.20 - flap, size * 0.58, -size * 0.02);
  ctx.lineTo(size * 0.43, size * 0.02);
  ctx.lineTo(size * 0.34, size * 0.18 + flap * 0.25);
  ctx.lineTo(size * 0.22, size * 0.08);
  ctx.lineTo(size * 0.10, size * 0.20);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  const bodyGradient = ctx.createRadialGradient(
    -size * 0.06,
    -size * 0.12,
    size * 0.02,
    0,
    0,
    size * 0.40
  );
  bodyGradient.addColorStop(0, "#4a1b20");
  bodyGradient.addColorStop(0.45, "#16080b");
  bodyGradient.addColorStop(1, "#030203");
  ctx.fillStyle = bodyGradient;
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.16, size * 0.33, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowColor = "rgba(255, 40, 18, 1)";
  ctx.shadowBlur = 12;
  ctx.fillStyle = "#ff351f";
  ctx.beginPath();
  ctx.ellipse(-size * 0.052, -size * 0.205, size * 0.026, size * 0.016, -0.15, 0, Math.PI * 2);
  ctx.ellipse(size * 0.052, -size * 0.205, size * 0.026, size * 0.016, 0.15, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const trail = ctx.createLinearGradient(player.x, player.y + size * 0.26, player.x, player.y + size * 0.92);
  trail.addColorStop(0, "rgba(255, 49, 24, 0.32)");
  trail.addColorStop(1, "rgba(255, 49, 24, 0)");
  ctx.fillStyle = trail;
  ctx.beginPath();
  ctx.moveTo(player.x - size * 0.08, player.y + size * 0.22);
  ctx.lineTo(player.x + size * 0.08, player.y + size * 0.22);
  ctx.lineTo(player.x + size * 0.20, player.y + size * 0.92);
  ctx.lineTo(player.x - size * 0.20, player.y + size * 0.92);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawCrashFlash(ctx, game) {
  if (!game.crashed) return;

  const pulse = 0.42;
  const radius = game.player.size * 1.35;
  const burst = ctx.createRadialGradient(
    game.player.x,
    game.player.y,
    0,
    game.player.x,
    game.player.y,
    radius
  );
  burst.addColorStop(0, `rgba(255, 240, 190, ${pulse})`);
  burst.addColorStop(0.28, "rgba(255, 67, 20, 0.34)");
  burst.addColorStop(1, "rgba(255, 0, 0, 0)");
  ctx.fillStyle = burst;
  ctx.beginPath();
  ctx.arc(game.player.x, game.player.y, radius, 0, Math.PI * 2);
  ctx.fill();
}

function batHitsRock(player, rock) {
  const halfWidth = player.size * 0.38;
  const halfHeight = player.size * 0.20;
  const dx = (rock.x - player.x) / (rock.radius + halfWidth);
  const dy = (rock.y - player.y) / (rock.radius + halfHeight);
  return dx * dx + dy * dy < 0.74;
}

function batCollectsPog(player, coin) {
  const dx = coin.x - player.x;
  const dy = coin.y - player.y;
  const radius = coin.radius + player.size * 0.27;
  return dx * dx + dy * dy <= radius * radius;
}

function createVisualEmbers(width, height, rng) {
  return Array.from({ length: 58 }, (_, index) => ({
    x: randomBetween(rng, 0, width),
    y: randomBetween(rng, 0, height),
    size: randomBetween(rng, 0.8, 2.6),
    speedFactor: randomBetween(rng, 0.34, 0.78),
    drift: randomBetween(rng, 2, 12),
    phase: index * 0.43,
    isStar: false,
  }));
}

function Batrunner({ onGameModeChange }) {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const gameRef = useRef(null);
  const inputRef = useRef({
    left: false,
    right: false,
    pointerDirection: 0,
    pointerHeld: false,
    tapImpulseUntil: 0,
  });
  const balanceAnimationRef = useRef(null);
  const reelImagesRef = useRef([]);
  const bgmAudioRef = useRef(null);
  const collectAudioRef = useRef(null);
  const collisionAudioRef = useRef(null);
  const phaseRef = useRef("LANDING");
  const balanceRef = useRef(0);

  const [phase, setPhase] = useState("LANDING");
  const [soundMuted, setSoundMuted] = useState(false);
  const [balance, setBalance] = useState(0);
  const [profileLoading, setProfileLoading] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [hudScore, setHudScore] = useState(0);
  const [hudPogs, setHudPogs] = useState(0);
  const [finalResult, setFinalResult] = useState({ score: 0, pogs: 0, durationMs: 0, collectedPogIds: [] });
  const [landingError, setLandingError] = useState("");
  const [settlementError, setSettlementError] = useState("");
  const formattedBalance = profileLoading ? "—" : formatPogs(balance);
  const {
    frameRef: arcadeWalletFrameRef,
    valueRef: arcadeWalletValueRef,
    scale: arcadeWalletScale,
  } = useArcadeWalletAutoScale(formattedBalance);

  phaseRef.current = phase;
  balanceRef.current = balance;

  const gameModeActive = phase !== "LANDING";

  useEffect(() => {
    if (typeof onGameModeChange === "function") {
      onGameModeChange(gameModeActive);
    }

    document.documentElement.classList.toggle("batrunner-lock-scroll", gameModeActive);
    document.body.classList.toggle("batrunner-lock-scroll", gameModeActive);

    return () => {
      document.documentElement.classList.remove("batrunner-lock-scroll");
      document.body.classList.remove("batrunner-lock-scroll");
    };
  }, [gameModeActive, onGameModeChange]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (balanceAnimationRef.current) {
        cancelAnimationFrame(balanceAnimationRef.current);
      }
      if (typeof onGameModeChange === "function") {
        onGameModeChange(false);
      }
      exitBrowserFullscreen();
    };
  }, [onGameModeChange]);

  useEffect(() => {
    const images = BATRUNNER_REEL_SOURCES.map((src) => {
      const image = new Image();
      image.decoding = "async";
      image.src = src;
      return image;
    });

    reelImagesRef.current = images;

    return () => {
      reelImagesRef.current = [];
    };
  }, []);

  // Batrunner audio is created once and retained for the life of the page so
  // START, coin collection, collision and mute state all share one sound system.
  useEffect(() => {
    const bgm = new Audio(batrunnerBgm);
    const collect = new Audio(collectSfx);
    const collision = new Audio(collisionSfx);

    bgm.loop = true;
    bgm.preload = "auto";
    bgm.volume = 0.62;

    collect.preload = "auto";
    collect.volume = 0.92;

    collision.preload = "auto";
    collision.volume = 1;

    bgm.muted = soundMuted;
    collect.muted = soundMuted;
    collision.muted = soundMuted;

    bgmAudioRef.current = bgm;
    collectAudioRef.current = collect;
    collisionAudioRef.current = collision;

    return () => {
      [bgm, collect, collision].forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });

      bgmAudioRef.current = null;
      collectAudioRef.current = null;
      collisionAudioRef.current = null;
    };
    // Audio instances must only be created once. Muting is handled separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    [bgmAudioRef.current, collectAudioRef.current, collisionAudioRef.current]
      .filter(Boolean)
      .forEach((audio) => {
        audio.muted = soundMuted;
      });
  }, [soundMuted]);

  const stopBgm = useCallback(() => {
    const audio = bgmAudioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
  }, []);

  const stopAllBatrunnerAudio = useCallback(() => {
    [bgmAudioRef.current, collectAudioRef.current, collisionAudioRef.current]
      .filter(Boolean)
      .forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
  }, []);

  const playBgm = useCallback(() => {
    const audio = bgmAudioRef.current;
    if (!audio) return;

    const collect = collectAudioRef.current;
    const collision = collisionAudioRef.current;

    [collect, collision].filter(Boolean).forEach((effect) => {
      effect.pause();
      effect.currentTime = 0;
    });

    audio.currentTime = 0;
    const playPromise = audio.play();

    if (playPromise?.catch) {
      playPromise.catch((error) => {
        console.warn("Batrunner BGM playback was blocked:", error);
      });
    }
  }, []);

  const playCollectSfx = useCallback(() => {
    const audio = collectAudioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    const playPromise = audio.play();

    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  }, []);

  const playCollisionSfx = useCallback(() => {
    const audio = collisionAudioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    const playPromise = audio.play();

    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  }, []);

  const handleSoundToggle = useCallback(() => {
    const nextMuted = !soundMuted;

    [bgmAudioRef.current, collectAudioRef.current, collisionAudioRef.current]
      .filter(Boolean)
      .forEach((audio) => {
        audio.muted = nextMuted;
      });

    setSoundMuted(nextMuted);

    // If the browser ever suspended the BGM, unmuting is a direct user gesture
    // and is therefore a safe opportunity to resume it during an active run.
    if (!nextMuted && phaseRef.current === "RUNNING") {
      const bgm = bgmAudioRef.current;
      if (bgm?.paused) {
        const playPromise = bgm.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      }
    }
  }, [soundMuted]);

  const restoreProfile = useCallback(async () => {
    setProfileLoading(true);
    setLandingError("");

    try {
      const profile = await getBatrunnerProfile();
      setBalance(Number(profile?.wallet?.pogs || 0));
      setPreviewMode(false);
    } catch (error) {
      if (isLocalPreviewHost()) {
        setPreviewMode(true);
        setBalance(DEV_PREVIEW_BALANCE);
      } else {
        console.error("Batrunner profile restore failed:", error);
        setLandingError(error?.message || "BATRUNNER PROFILE UNAVAILABLE");
      }
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreProfile();
  }, [restoreProfile]);

  const animateBalance = useCallback((fromValue, toValue) => {
    if (balanceAnimationRef.current) {
      cancelAnimationFrame(balanceAnimationRef.current);
    }

    const from = Math.max(0, Math.floor(Number(fromValue || 0)));
    const to = Math.max(0, Math.floor(Number(toValue || 0)));

    if (to <= from) {
      setBalance(to);
      return;
    }

    const difference = to - from;
    const duration = clamp(420 + difference * 70, 520, 2200);
    const startedAt = performance.now();

    const tick = (now) => {
      const progress = clamp((now - startedAt) / duration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.min(to, from + Math.floor(difference * eased));
      setBalance(nextValue);

      if (progress < 1) {
        balanceAnimationRef.current = requestAnimationFrame(tick);
      } else {
        setBalance(to);
        balanceAnimationRef.current = null;
      }
    };

    setBalance(from);
    balanceAnimationRef.current = requestAnimationFrame(tick);
  }, []);

  const stopGameLoop = useCallback(() => {
    if (gameRef.current) {
      gameRef.current.running = false;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const crashRun = useCallback(() => {
    const game = gameRef.current;
    if (!game || !game.running || game.crashed) return;

    game.crashed = true;
    game.running = false;

    // Impact audio is immediate: stop the looping BGM first, then fire the
    // collision hit exactly once before the GAME OVER state is shown.
    stopBgm();
    playCollisionSfx();

    const durationMs = Math.max(0, Math.floor(game.elapsedMs));
    const score = Math.max(0, Math.floor((durationMs / 1000) * SCORE_PER_SECOND));
    const collectedPogIds = Array.from(game.collectedPogIds);

    drawCrashFlash(game.ctx, game);

    phaseRef.current = "CRASHED";
    setHudScore(score);
    setHudPogs(collectedPogIds.length);
    setFinalResult({
      score,
      pogs: collectedPogIds.length,
      durationMs,
      collectedPogIds,
    });
    setSettlementError("");
    setPhase("CRASHED");
  }, [playCollisionSfx, stopBgm]);

  const resizeGame = useCallback(() => {
    const canvas = canvasRef.current;
    const game = gameRef.current;
    if (!canvas || !game) return;

    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width || window.innerWidth));
    const height = Math.max(1, Math.round(rect.height || window.innerHeight));
    const dpr = clamp(window.devicePixelRatio || 1, 1, 2);

    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }

    game.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    game.width = width;
    game.height = height;
    game.dpr = dpr;

    const corridorWidth = Math.min(width, 980);
    game.corridorLeft = (width - corridorWidth) / 2;
    game.corridorRight = game.corridorLeft + corridorWidth;

    if (!game.player.initialized) {
      game.player.x = width / 2;
      game.player.y = height * 0.80;
      game.player.initialized = true;
      game.embers = createVisualEmbers(width, height, game.visualRng);
    } else {
      game.player.y = height * 0.80;
      game.player.x = clamp(
        game.player.x,
        game.corridorLeft + game.player.size * 0.48,
        game.corridorRight - game.player.size * 0.48
      );
    }

    game.player.size = clamp(corridorWidth * 0.105, 68, 104);
  }, []);

  const spawnRock = useCallback((game) => {
    const corridorWidth = game.corridorRight - game.corridorLeft;
    const radius = randomBetween(
      game.rockRng,
      clamp(corridorWidth * 0.037, 24, 34),
      clamp(corridorWidth * 0.075, 42, 64)
    );

    let x = randomBetween(
      game.rockRng,
      game.corridorLeft + radius + 18,
      game.corridorRight - radius - 18
    );

    if (
      Number.isFinite(game.lastRockX) &&
      game.elapsedMs - game.lastRockSpawnMs < 850 &&
      Math.abs(x - game.lastRockX) < radius * 2.5
    ) {
      const corridorMiddle = (game.corridorLeft + game.corridorRight) / 2;
      const mirrorX = corridorMiddle - (x - corridorMiddle);
      x = clamp(
        mirrorX,
        game.corridorLeft + radius + 18,
        game.corridorRight - radius - 18
      );
    }

    game.lastRockX = x;
    game.lastRockSpawnMs = game.elapsedMs;

    game.rocks.push({
      x,
      y: -radius - 40,
      radius,
      rotation: randomBetween(game.rockRng, 0, Math.PI * 2),
      rotationSpeed: randomBetween(game.rockRng, -1.35, 1.35),
      speedFactor: randomBetween(game.rockRng, 0.94, 1.10),
      shape: buildRockShape(game.rockRng),
      // Freeze the environment style at spawn-time so rocks already on-screen
      // do not recolour when a reel boundary is crossed.
      isAsteroid: Boolean(game.asteroidMode),
    });
  }, []);

  const spawnPog = useCallback((game) => {
    const corridorWidth = game.corridorRight - game.corridorLeft;
    const radius = clamp(corridorWidth * 0.031, 21, 30);
    game.pogCounter += 1;

    game.coins.push({
      id: `pog_${game.pogCounter}`,
      x: randomBetween(
        game.pogXRng,
        game.corridorLeft + radius + 20,
        game.corridorRight - radius - 20
      ),
      y: -radius - 30,
      radius,
      rotation: randomBetween(game.pogXRng, 0, Math.PI * 2),
      rotationSpeed: randomBetween(game.pogXRng, 4.6, 7.2),
    });
  }, []);

  const updateGame = useCallback((game, dt, now) => {
    const input = inputRef.current;
    const pointerActive = input.pointerHeld || now < input.tapImpulseUntil;
    let direction = 0;

    if (input.left) direction -= 1;
    if (input.right) direction += 1;
    if (pointerActive && direction === 0) direction = input.pointerDirection;

    const targetVelocity = direction * clamp(game.width * 0.70, 390, 690);
    const acceleration = clamp(game.width * 3.3, 1800, 3400);

    if (direction !== 0) {
      const deltaVelocity = targetVelocity - game.player.vx;
      const maxChange = acceleration * dt;
      game.player.vx += clamp(deltaVelocity, -maxChange, maxChange);
    } else {
      const drag = acceleration * 1.16 * dt;
      if (Math.abs(game.player.vx) <= drag) game.player.vx = 0;
      else game.player.vx -= Math.sign(game.player.vx) * drag;
    }

    game.player.x += game.player.vx * dt;
    const halfBat = game.player.size * 0.48;
    const minX = game.corridorLeft + halfBat;
    const maxX = game.corridorRight - halfBat;

    if (game.player.x <= minX || game.player.x >= maxX) {
      game.player.x = clamp(game.player.x, minX, maxX);
      game.player.vx *= 0.18;
    }

    // Derive the environment mode from the exact same reel geometry used by
    // drawBackground. This keeps the visual transition locked to the authored
    // Reel07/Reel01 boundaries regardless of frame rate or game speed.
    game.asteroidMode = isAsteroidReelPhase(game);

    const seconds = game.elapsedMs / 1000;
    const minRockGap = Math.max(430, 970 - seconds * 6.1);
    const maxRockGap = Math.max(720, 1450 - seconds * 5.2);

    while (game.elapsedMs >= game.nextRockAtMs) {
      spawnRock(game);
      game.nextRockAtMs += randomIntInclusive(
        game.rockRng,
        Math.floor(minRockGap),
        Math.floor(maxRockGap)
      );
    }

    while (game.elapsedMs >= game.nextPogAtMs) {
      spawnPog(game);
      game.nextPogAtMs += randomIntInclusive(
        game.pogTimeRng,
        POG_SPAWN_MIN_MS,
        POG_SPAWN_MAX_MS
      );
    }

    const worldSpeed = getWorldSpeed(game);

    game.rocks.forEach((rock) => {
      rock.y += worldSpeed * rock.speedFactor * dt;
      rock.rotation += rock.rotationSpeed * dt;
    });

    game.coins.forEach((coin) => {
      coin.y += worldSpeed * 0.90 * dt;
      coin.rotation += coin.rotationSpeed * dt;
    });

    for (const rock of game.rocks) {
      if (batHitsRock(game.player, rock)) {
        crashRun();
        return;
      }
    }

    const survivingCoins = [];
    game.coins.forEach((coin) => {
      if (batCollectsPog(game.player, coin)) {
        if (!game.collectedPogIds.has(coin.id)) {
          game.collectedPogIds.add(coin.id);
          setHudPogs(game.collectedPogIds.size);
          playCollectSfx();
        }
      } else if (coin.y < game.height + coin.radius + 40) {
        survivingCoins.push(coin);
      }
    });

    game.coins = survivingCoins;
    game.rocks = game.rocks.filter((rock) => rock.y < game.height + rock.radius + 80);

    updateEmbers(game, dt);
  }, [crashRun, playCollectSfx, spawnPog, spawnRock]);

  const drawGame = useCallback((game) => {
    const ctx = game.ctx;
    ctx.clearRect(0, 0, game.width, game.height);

    drawBackground(ctx, game);
    drawSpeedStreaks(ctx, game);
    drawCorridor(ctx, game);

    game.coins.forEach((coin) => drawPog(ctx, game, coin));
    game.rocks.forEach((rock) => drawRock(ctx, rock));
    drawBat(ctx, game);
    drawEmbers(ctx, game);

    if (game.crashed) drawCrashFlash(ctx, game);
  }, []);

  useEffect(() => {
    if (phase !== "RUNNING" || !activeRun || !canvasRef.current) return undefined;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!ctx) {
      setLandingError("BATRUNNER CANVAS UNAVAILABLE");
      setPhase("LANDING");
      return undefined;
    }

    const pogImageObject = new Image();
    pogImageObject.src = pogImage;

    const seed = Number(activeRun.seed) >>> 0;
    const pogTimeRng = createRng(seed ^ POG_TIME_SEED_XOR);
    const visualRng = createRng(seed ^ 0x5bd1e995);

    const game = {
      ctx,
      width: 1,
      height: 1,
      dpr: 1,
      corridorLeft: 0,
      corridorRight: 1,
      player: {
        x: 0,
        y: 0,
        vx: 0,
        size: 78,
        initialized: false,
      },
      rocks: [],
      coins: [],
      embers: [],
      collectedPogIds: new Set(),
      rockRng: createRng(seed ^ ROCK_SEED_XOR),
      pogTimeRng,
      pogXRng: createRng(seed ^ POG_X_SEED_XOR),
      visualRng,
      worldSeed: seed ^ 0x7f4a7c15,
      nextRockAtMs: 850,
      nextPogAtMs: randomIntInclusive(pogTimeRng, POG_SPAWN_MIN_MS, POG_SPAWN_MAX_MS),
      pogCounter: 0,
      lastRockX: Number.NaN,
      lastRockSpawnMs: -10000,
      elapsedMs: 0,
      startedPerf: performance.now(),
      lastFramePerf: performance.now(),
      lastHudPerf: 0,
      running: true,
      crashed: false,
      asteroidMode: false,
      reelImages: reelImagesRef.current,
      pogImage: pogImageObject,
    };

    gameRef.current = game;
    resizeGame();

    const frame = (now) => {
      if (!game.running || phaseRef.current !== "RUNNING") return;

      const dt = clamp((now - game.lastFramePerf) / 1000, 0, MAX_DT_SECONDS);
      game.lastFramePerf = now;
      game.elapsedMs = Math.max(0, now - game.startedPerf);

      updateGame(game, dt, now);
      drawGame(game);

      if (now - game.lastHudPerf >= HUD_REFRESH_MS) {
        game.lastHudPerf = now;
        setHudScore(Math.floor((game.elapsedMs / 1000) * SCORE_PER_SECOND));
        setHudPogs(game.collectedPogIds.size);
      }

      if (game.running && phaseRef.current === "RUNNING") {
        animationFrameRef.current = requestAnimationFrame(frame);
      }
    };

    const onResize = () => resizeGame();
    window.addEventListener("resize", onResize, { passive: true });
    animationFrameRef.current = requestAnimationFrame(frame);

    return () => {
      game.running = false;
      window.removeEventListener("resize", onResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [activeRun, drawGame, phase, resizeGame, updateGame]);

  useEffect(() => {
    if (phase !== "RUNNING") return undefined;

    const input = inputRef.current;

    const onKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        input.left = true;
        event.preventDefault();
      }
      if (event.key === "ArrowRight") {
        input.right = true;
        event.preventDefault();
      }
    };

    const onKeyUp = (event) => {
      if (event.key === "ArrowLeft") {
        input.left = false;
        event.preventDefault();
      }
      if (event.key === "ArrowRight") {
        input.right = false;
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", onKeyDown, { passive: false });
    window.addEventListener("keyup", onKeyUp, { passive: false });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      input.left = false;
      input.right = false;
    };
  }, [phase]);

  const handlePointerDown = useCallback((event) => {
    if (phaseRef.current !== "RUNNING") return;

    const canvas = canvasRef.current;
    const game = gameRef.current;
    if (!canvas || !game) return;

    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    inputRef.current.pointerDirection = pointerX < game.player.x ? -1 : 1;
    inputRef.current.pointerHeld = true;
    inputRef.current.tapImpulseUntil = performance.now() + TAP_IMPULSE_MS;

    try {
      canvas.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Pointer capture is optional.
    }
  }, []);

  const handlePointerMove = useCallback((event) => {
    if (phaseRef.current !== "RUNNING" || !inputRef.current.pointerHeld) return;

    const canvas = canvasRef.current;
    const game = gameRef.current;
    if (!canvas || !game) return;

    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    inputRef.current.pointerDirection = pointerX < game.player.x ? -1 : 1;
  }, []);

  const handlePointerUp = useCallback((event) => {
    if (phaseRef.current !== "RUNNING") return;

    event.preventDefault();
    inputRef.current.pointerHeld = false;
    inputRef.current.tapImpulseUntil = performance.now() + TAP_IMPULSE_MS;
  }, []);

  const handleStart = useCallback(async () => {
    if (phaseRef.current !== "LANDING") return;

    setLandingError("");
    setSettlementError("");
    phaseRef.current = "STARTING";
    setHudScore(0);
    setHudPogs(0);
    setFinalResult({ score: 0, pogs: 0, durationMs: 0, collectedPogIds: [] });
    setPhase("STARTING");

    // START is a direct user gesture, so begin the looping BGM here rather than
    // waiting for the asynchronous run API to complete.
    playBgm();

    // Must be requested directly from the START user gesture on mobile.
    enterMobileFullscreen();

    try {
      if (previewMode) {
        const randomSeedArray = new Uint32Array(1);
        window.crypto?.getRandomValues?.(randomSeedArray);
        const seed = randomSeedArray[0] || (Date.now() >>> 0);
        setActiveRun({
          runId: `preview_batrunner_${Date.now().toString(36)}`,
          seed,
          preview: true,
        });
        phaseRef.current = "RUNNING";
        setPhase("RUNNING");
        return;
      }

      const response = await startBatrunnerRun();
      setBalance(Number(response?.balance ?? balanceRef.current));
      setActiveRun({
        runId: response?.run?.runId,
        seed: Number(response?.run?.seed) >>> 0,
        preview: false,
      });
      phaseRef.current = "RUNNING";
      setPhase("RUNNING");
    } catch (error) {
      if (isLocalPreviewHost()) {
        const randomSeedArray = new Uint32Array(1);
        window.crypto?.getRandomValues?.(randomSeedArray);
        setPreviewMode(true);
        setActiveRun({
          runId: `preview_batrunner_${Date.now().toString(36)}`,
          seed: randomSeedArray[0] || (Date.now() >>> 0),
          preview: true,
        });
        phaseRef.current = "RUNNING";
        setPhase("RUNNING");
        return;
      }

      console.error("Batrunner start failed:", error);
      setLandingError(String(error?.message || "BATRUNNER START FAILED").toUpperCase());
      setActiveRun(null);
      phaseRef.current = "LANDING";
      setPhase("LANDING");
      stopAllBatrunnerAudio();
      exitBrowserFullscreen();
    }
  }, [playBgm, previewMode, stopAllBatrunnerAudio]);

  const handleGameOverClose = useCallback(async () => {
    if (phaseRef.current !== "CRASHED" || !activeRun) return;

    stopGameLoop();
    stopAllBatrunnerAudio();
    setSettlementError("");
    phaseRef.current = "SETTLING";
    setPhase("SETTLING");

    try {
      const startingBalance = balanceRef.current;
      let nextBalance = startingBalance;

      if (activeRun.preview) {
        nextBalance = startingBalance + finalResult.pogs;
      } else {
        const response = await settleBatrunnerRun({
          runId: activeRun.runId,
          durationMs: finalResult.durationMs,
          score: finalResult.score,
          collectedPogIds: finalResult.collectedPogIds,
        });
        nextBalance = Number(response?.balance ?? startingBalance);
      }

      await exitBrowserFullscreen();
      setActiveRun(null);
      gameRef.current = null;
      phaseRef.current = "LANDING";
      setPhase("LANDING");
      animateBalance(startingBalance, nextBalance);
    } catch (error) {
      console.error("Batrunner settlement failed:", error);
      setSettlementError(
        String(error?.message || "POG SETTLEMENT FAILED — PRESS CLOSE TO RETRY").toUpperCase()
      );
      phaseRef.current = "CRASHED";
      setPhase("CRASHED");
    }
  }, [activeRun, animateBalance, finalResult, stopAllBatrunnerAudio, stopGameLoop]);

  if (phase === "LANDING") {
    return (
      <div className="batrunner-shell">
        <Helmet>
          <title>Batrunner - Pogforts</title>
          <meta
            name="description"
            content="Batrunner is the Pogforts endless flight game. Dodge the inferno, collect Pogs and survive as long as you can."
          />
        </Helmet>

        <section className="batrunner-panel">
          <div className="batrunner-topbar pogforts-arcade-topbar">
            <div>
              <p className="batrunner-kicker">POGFORTS ARCADE</p>
              <h1 className="batrunner-title">BATRUNNER</h1>
              <p className="batrunner-subtitle">FLY TO INFINITY</p>
            </div>

            <div className="batrunner-wallet pogforts-arcade-wallet">
              <span>{previewMode ? "PREVIEW POG BALANCE" : "POG BALANCE"}</span>
              <strong ref={arcadeWalletFrameRef}>
                <span
                  ref={arcadeWalletValueRef}
                  className="pogforts-arcade-wallet-value"
                  style={{ "--pog-wallet-scale": arcadeWalletScale }}
                >
                  {!profileLoading && Number(balance) !== 0 && (
                    <img src={pogImage} alt="" aria-hidden="true" draggable="false" />
                  )}
                  {formattedBalance}
                </span>
              </strong>
            </div>
          </div>

          <div className="batrunner-landing-stage">
            <img
              className="batrunner-landing-art"
              src={pogfortsBackground}
              alt="Pogforts inferno"
              draggable="false"
            />
            <div className="batrunner-landing-shade" />
            <div className="batrunner-landing-content">
              <div className="batrunner-landing-bat" aria-hidden="true">
                <span className="batrunner-landing-bat-wing batrunner-landing-bat-wing-left" />
                <span className="batrunner-landing-bat-body" />
                <span className="batrunner-landing-bat-wing batrunner-landing-bat-wing-right" />
              </div>

              <h2>FLY. DODGE. COLLECT.</h2>
              <p>
                Fly through the realm avoiding rocks, collect rare Pogs and keep the run alive to drive your score higher.
              </p>

              <div className="batrunner-control-grid">
                <div>
                  <span>DESKTOP</span>
                  <strong>← → OR CLICK LEFT / RIGHT</strong>
                </div>
                <div>
                  <span>MOBILE / TABLET</span>
                  <strong>TAP LEFT / RIGHT OF THE BAT</strong>
                </div>
              </div>

              {previewMode && (
                <p className="batrunner-preview-note">
                  DEVELOPMENT PREVIEW — MOCK {formatPogs(DEV_PREVIEW_BALANCE)} POG WALLET — NO DATABASE
                </p>
              )}

              {landingError && (
                <p className="batrunner-error" role="alert">{landingError}</p>
              )}

              <button
                type="button"
                className="batrunner-start-button"
                onClick={handleStart}
                disabled={profileLoading && !previewMode}
              >
                START
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="batrunner-runtime" role="application" aria-label="Batrunner game">
      <canvas
        ref={canvasRef}
        className="batrunner-canvas"
        aria-label="Batrunner flight area. Use left and right arrow keys, mouse clicks, or touch controls to steer."
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onContextMenu={(event) => event.preventDefault()}
      />

      <div className="batrunner-runtime-vignette" aria-hidden="true" />

      <button
        type="button"
        className="pogforts-arcade-sound-toggle"
        aria-label={soundMuted ? "Unmute Batrunner sound" : "Mute Batrunner sound"}
        title={soundMuted ? "Sound off — click to unmute" : "Sound on — click to mute"}
        onClick={handleSoundToggle}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <span aria-hidden="true">{soundMuted ? "🔇" : "🔊"}</span>
      </button>

      <div className="batrunner-hud" aria-live="polite">
        <div className="batrunner-hud-stat">
          <span>SCORE</span>
          <strong>{formatPogs(hudScore)}</strong>
        </div>
        <div className="batrunner-hud-stat batrunner-hud-pogs">
          <span>POGS</span>
          <strong>
            {hudPogs > 0 && <img src={pogImage} alt="" aria-hidden="true" draggable="false" />}
            {formatPogs(hudPogs)}
          </strong>
        </div>
      </div>

      {phase === "STARTING" && (
        <div className="batrunner-starting-overlay">
          <div className="batrunner-starting-pulse" aria-hidden="true" />
          <strong>IGNITING BATRUNNER</strong>
        </div>
      )}

      {(phase === "CRASHED" || phase === "SETTLING") && (
        <div className="batrunner-gameover-layer" role="dialog" aria-modal="true" aria-labelledby="batrunner-gameover-title">
          <div className="batrunner-gameover-modal">
            <p className="batrunner-kicker">POGFORTS</p>
            <h2 id="batrunner-gameover-title">GAME OVER</h2>

            <div className="batrunner-gameover-stats">
              <div>
                <span>SCORE</span>
                <strong>{formatPogs(finalResult.score)}</strong>
              </div>
              <div>
                <span>POGS COLLECTED</span>
                <strong className="batrunner-gameover-pogs">
                  {finalResult.pogs > 0 && <img src={pogImage} alt="" aria-hidden="true" draggable="false" />}
                  {formatPogs(finalResult.pogs)}
                </strong>
              </div>
            </div>

            {settlementError && (
              <p className="batrunner-gameover-error" role="alert">{settlementError}</p>
            )}

            <button
              type="button"
              className="batrunner-gameover-close"
              onClick={handleGameOverClose}
              disabled={phase === "SETTLING"}
            >
              {phase === "SETTLING" ? "BANKING POGS..." : "CLOSE"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Batrunner;
